import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts'

interface GoalTypeData {
  type: string
  count: number
  percentage: number
}

interface PerformanceData {
  date: string
  wins: number
  losses: number
  points: number
}

interface RoleData {
  role: string
  games: number
  wins: number
  winRate: number
}

interface StatsChartsProps {
  goalsByType?: Array<{ goal_type: string; count: number }>
  performanceOverTime?: Array<{ date: string; result: string; points: number }>
  roleBreakdown?: Array<{ role: string; games: number; wins: number }>
}

const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899']
const GOAL_TYPE_LABELS: Record<string, string> = {
  'attack_shot': 'Attack',
  'midfield_shot': 'Midfield',
  'defense_shot': 'Defense',
  'goalkeeper_shot': 'Goalkeeper',
  'monsterblock_defense': 'Monsterblock (D)',
  'monsterblock_goalkeeper': 'Monsterblock (GK)',
  'gamelle_attack': 'Gamelle (A)',
  'gamelle_midfield': 'Gamelle (M)',
  'gamelle_defense': 'Gamelle (D)',
  'gamelle_goalkeeper': 'Gamelle (GK)',
  'own_goal_attack': 'Own Goal (A)',
  'own_goal_midfield': 'Own Goal (M)',
  'own_goal_defense': 'Own Goal (D)',
  'own_goal_goalkeeper': 'Own Goal (GK)',
}

export function StatsCharts({ goalsByType, performanceOverTime, roleBreakdown }: StatsChartsProps) {
  // Process goals by type data
  const goalTypeData: GoalTypeData[] = goalsByType
    ? goalsByType.map(g => {
        const total = goalsByType.reduce((sum, item) => sum + parseInt(item.count as any), 0)
        return {
          type: GOAL_TYPE_LABELS[g.goal_type] || g.goal_type,
          count: parseInt(g.count as any),
          percentage: Math.round((parseInt(g.count as any) / total) * 100)
        }
      })
    : []

  // Process performance over time
  const performanceData: PerformanceData[] = performanceOverTime
    ? performanceOverTime.reduce((acc: PerformanceData[], game) => {
        const date = new Date(game.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const existing = acc.find(d => d.date === date)
        
        if (existing) {
          if (game.result === 'win') {
            existing.wins++
            existing.points += game.points
          } else {
            existing.losses++
          }
        } else {
          acc.push({
            date,
            wins: game.result === 'win' ? 1 : 0,
            losses: game.result === 'loss' ? 1 : 0,
            points: game.result === 'win' ? game.points : 0
          })
        }
        
        return acc
      }, [])
    : []

  // Process role breakdown
  const roleData: RoleData[] = roleBreakdown
    ? roleBreakdown.map(r => ({
        role: r.role.charAt(0).toUpperCase() + r.role.slice(1),
        games: r.games,
        wins: r.wins,
        winRate: r.games > 0 ? Math.round((r.wins / r.games) * 100) : 0
      }))
    : []

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    if (percent < 0.05) return null

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs font-semibold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Goals by Type - Pie Chart */}
      {goalTypeData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Goals by Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={goalTypeData as any}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {goalTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any, name: any, props: any) => [`${value} goals (${props.payload.percentage}%)`, props.payload.type]} />
              <Legend 
                formatter={(value, entry: any) => `${entry.payload.type} (${entry.payload.count})`}
                wrapperStyle={{ fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Performance Over Time - Line Chart */}
      {performanceData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" style={{ fontSize: '12px' }} />
              <YAxis style={{ fontSize: '12px' }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line type="monotone" dataKey="wins" stroke="#10b981" strokeWidth={2} name="Wins" />
              <Line type="monotone" dataKey="losses" stroke="#ef4444" strokeWidth={2} name="Losses" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Role Performance - Bar Chart */}
      {roleData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance by Role</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={roleData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="role" style={{ fontSize: '12px' }} />
              <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" style={{ fontSize: '12px' }} />
              <YAxis yAxisId="right" orientation="right" stroke="#10b981" style={{ fontSize: '12px' }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar yAxisId="left" dataKey="games" fill="#3b82f6" name="Games Played" />
              <Bar yAxisId="right" dataKey="winRate" fill="#10b981" name="Win Rate (%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Win/Loss Distribution */}
      {performanceData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Win/Loss Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Wins', value: performanceData.reduce((sum, d) => sum + d.wins, 0) },
                  { name: 'Losses', value: performanceData.reduce((sum, d) => sum + d.losses, 0) }
                ]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                <Cell fill="#10b981" />
                <Cell fill="#ef4444" />
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '14px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

